import { DoNotReportError } from "@/helpers/sentry";

export class OfflineError extends Error {
  constructor(
    message: string,
    public readonly extra?: object
  ) {
    super(message);
    this.name = "OfflineError";
  }
}

export class WpsHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly extra?: object
  ) {
    super(message);
    this.name = `WpsHttpError_${status}`;
  }
}

// 4xx

export class ClientError extends WpsHttpError {
  constructor(
    message: string,
    status: number,
    statusText: string,
    extra?: object
  ) {
    super(message, status, statusText, extra);
    this.name = `ClientError_${status}`;
  }
}

export class BadRequestError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Bad request to WPS", 400, statusText, extra);
    this.name = "BadRequestError";
  }
}

export class UnauthorizedError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Unauthorized access to WPS resource", 401, statusText, extra);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Forbidden access to WPS resource", 403, statusText, extra);
    this.name = "ForbiddenError";
  }
}

export class NotFoundError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("WPS resource not found", 404, statusText, extra);
    this.name = "NotFoundError";
  }
}

export class RequestTimeoutError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Request to WPS timed out", 408, statusText, extra);
    this.name = "RequestTimeoutError";
  }
}

export class ConflictError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Conflict on WPS resource", 409, statusText, extra);
    this.name = "ConflictError";
  }
}

export class GoneError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("WPS resource is gone", 410, statusText, extra);
    this.name = "GoneError";
  }
}

export class PayloadTooLargeError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Payload too large for WPS", 413, statusText, extra);
    this.name = "PayloadTooLargeError";
  }
}

export class UnprocessableEntityError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Unprocessable entity for WPS", 422, statusText, extra);
    this.name = "UnprocessableEntityError";
  }
}

export class TooManyRequestsError extends ClientError {
  constructor(statusText: string, extra?: object) {
    super("Too many requests to WPS", 429, statusText, extra);
    this.name = "TooManyRequestsError";
  }
}

// 5xx

export class ServerError extends WpsHttpError {
  constructor(
    message: string,
    status: number,
    statusText: string,
    extra?: object
  ) {
    super(message, status, statusText, extra);
    this.name = `ServerError_${status}`;
  }
}

export class InternalServerError extends ServerError {
  constructor(statusText: string, extra?: object) {
    super("WPS internal server error", 500, statusText, extra);
    this.name = "InternalServerError";
  }
}

export class BadGatewayError extends ServerError {
  constructor(statusText: string, extra?: object) {
    super("WPS bad gateway", 502, statusText, extra);
    this.name = "BadGatewayError";
  }
}

export class ServiceUnavailableError extends ServerError {
  constructor(statusText: string, extra?: object) {
    super("WPS service unavailable", 503, statusText, extra);
    this.name = "ServiceUnavailableError";
  }
}

export class GatewayTimeoutError extends ServerError {
  constructor(statusText: string, extra?: object) {
    super("WPS gateway timeout", 504, statusText, extra);
    this.name = "GatewayTimeoutError";
  }
}

export function httpErrorFromResponse(
  response: Response,
  extra?: object
): WpsHttpError {
  const { status, statusText } = response;
  switch (status) {
    case 400:
      return new BadRequestError(statusText, extra);
    case 401:
      return new UnauthorizedError(statusText, extra);
    case 403:
      return new ForbiddenError(statusText, extra);
    case 404:
      return new NotFoundError(statusText, extra);
    case 408:
      return new RequestTimeoutError(statusText, extra);
    case 409:
      return new ConflictError(statusText, extra);
    case 410:
      return new GoneError(statusText, extra);
    case 413:
      return new PayloadTooLargeError(statusText, extra);
    case 422:
      return new UnprocessableEntityError(statusText, extra);
    case 429:
      return new TooManyRequestsError(statusText, extra);
    case 500:
      return new InternalServerError(statusText, extra);
    case 502:
      return new BadGatewayError(statusText, extra);
    case 503:
      return new ServiceUnavailableError(statusText, extra);
    case 504:
      return new GatewayTimeoutError(statusText, extra);
  }

  if (status >= 400 && status < 500) {
    return new ClientError(
      `WPS client error [${status}]`,
      status,
      statusText,
      extra
    );
  }
  if (status >= 500 && status < 600) {
    return new ServerError(
      `WPS server error [${status}]`,
      status,
      statusText,
      extra
    );
  }

  // 1xx, 3xx, or non-standard status (e.g. 0 from opaque responses)
  return new WpsHttpError(
    `Unexpected WPS response [${status}]`,
    status,
    statusText,
    extra
  );
}

export class WpsBackendError
  extends Error
  // this error type is linked to an Error sent to Sentry from the Cloudflare Worker
  // there's no use in reporting it again from the front-end, as we already have all the details in Sentry
  implements DoNotReportError
{
  public readonly doNotReportError: true = true;

  constructor(public eventId: string) {
    super("Error from WPS backend");
    this.name = "WpsBackendError";
  }
}
